import { createBlankEvent, type FindFn } from "@shared/models/analytics/base"
import type { CourseEditFollowedCourse } from "@shared/models/analytics/course"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { FollowedCourse } from "@shared/models/course"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import { partial } from "lodash-es"
import type { PreparedAction } from "../../action-doc-builder"
import type { Narrative } from "../create-narrative"
import defaultCourseActivity from "./default-course-activity"
import defaultCourseContext from "./default-course-context"
import defaultCourseModels from "./default-course-models"

interface Payload {
  planbookId: string
  courseId: string
  courseTitle: string
  followedCourseInfo: FollowedCourse
  followedCourseTitle: string
}

export function prepareCourseFollowCourse(payload: Payload): PreparedAction {
  return {
    name: "COURSE_FOLLOW_COURSE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  return [
    Patch.create("course", payload.courseId).set(
      "attributes.followedCourse",
      payload.followedCourseInfo,
    ).patch,
  ]
}

function undoPatches(payload: Payload): Array<PatchModel> {
  return [Patch.create("course", payload.courseId).set("attributes.followedCourse", null).patch]
}

/** In every follow-a-class action's context, so `context.feature = 'follow_a_class'` finds them all. */
export function followContext({
  followingCourseId,
  followedCourseId,
}: {
  followingCourseId: string
  followedCourseId: string | null
}) {
  return { feature: "follow_a_class", followingCourseId, followedCourseId } as const
}

export function followedCourseEvent(
  findFn: FindFn,
  userId: string,
  adverbialVerb: "adding" | "removing",
  {
    planbookId,
    courseId,
    followedCourseId,
  }: { planbookId: string; courseId: string; followedCourseId: string | null },
): CourseEditFollowedCourse {
  const followedCourse = followedCourseId ? findFn("course", followedCourseId) : undefined
  const followedPlanbookId = followedCourse?.relationships.planbook?.data.id

  return {
    ...createBlankEvent(),
    root_id: planbookId,
    root_type: "planbook",
    date_plan_scheduled_for: null,

    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, planbookId),

    primary_object_type: "course",
    primary_object_id: courseId,
    primary_object_specifier: "followed_course",
    primary_object_id_and_ancestor_ids: [planbookId, courseId],

    adverbial_verb: adverbialVerb,

    adverbial_object_type: "course",
    adverbial_object_id: followedCourseId,
    adverbial_possessive: followedPlanbookId
      ? getPossessiveForPlanbook(findFn, userId, followedPlanbookId)
      : "unknown",
    adverbial_object_specifier: null,
    adverbial_object_id_and_ancestor_ids:
      followedPlanbookId && followedCourseId ? [followedPlanbookId, followedCourseId] : null,
  }
}

function narrative(payload: Payload, findFn: FindFn, userId): Narrative {
  let models = defaultCourseModels(payload, findFn, userId)

  let context = {
    ...defaultCourseContext(payload, models),
    ...followContext({
      followingCourseId: payload.courseId,
      followedCourseId: payload.followedCourseInfo.courseId,
    }),
    followedCourseInfo: payload.followedCourseInfo,
  }

  return {
    title: "Followed Class",
    titleForAnalytics: "Course - Changed Followed Course",
    description: `Your "${payload.courseTitle}" class started following "${payload.followedCourseTitle}"`,
    context,
    activity: defaultCourseActivity(payload, models),
    newAnalyticsEvents: [
      followedCourseEvent(findFn, userId, "adding", {
        planbookId: payload.planbookId,
        courseId: payload.courseId,
        followedCourseId: payload.followedCourseInfo.courseId,
      }),
    ],
  }
}
